import enableEditStateMeetingType from '../actions/enableEditStateMeetingType';
import setEditStatePrivacyType from '../actions/setEditStatePrivacyType';
import setMeetingTypeAvailability from '../actions/setMeetingTypeAvailability';
import setMeetingTypeCategory from '../actions/setMeetingTypeCategory';
import setMeetingTypeCharm from '../actions/setMeetingTypeCharm';
import setMeetingTypeCustomTimeInterval from '../actions/setMeetingTypeCustomTimeInterval';
import setMeetingTypeDescription from '../actions/setMeetingTypeDescription';
import setMeetingTypeOrigin from '../actions/setMeetingTypeOrigin';
import setMeetingTypeDuration from '../actions/setMeetingTypeDuration';
import setMeetingTypeLocation from '../actions/setMeetingTypeLocation';
import { setMeetingTypeMaxLeadTime } from '../actions/setMeetingTypeMaxLeadTime';
import { setMeetingTypeMinLeadTime } from '../actions/setMeetingTypeMinLeadTime';
import { setMeetingTypeName } from '../actions/setMeetingTypeName';
import { setMeetingTypeInboxReminders } from '../actions/setInboxReminders';
import setMeetingTypePostBuffer from '../actions/setMeetingTypePostBuffer';
import setMeetingTypePreBuffer from '../actions/setMeetingTypePreBuffer';
import setOnlineMeetingLocation from '../actions/setOnlineMeetingLocation';
import BookingPrivacyType from '../store/schema/BookingPrivacyType';
import { AvailabilityType } from '../store/schema/MeetingTypeEditOptionState';
import store from '../store/Store';
import { parseMeetingTypeDescription } from './parseServiceDescription';
import { DEFAULT_PALETTEID } from 'bookwithme-shared';
import { owaDate } from 'owa-datetime';
import type { IWorkDay } from 'owa-scheduling-hours';
import { getUserTimeZone } from 'owa-session-store';
import {
    updateCurrentEventThemeImageAction,
    updateCurrentEventEmojiAction,
} from '../actions/setMeetingTypeEditState';
import { convertInboxRemindersToGql } from 'convert-calendar-item';
import type BookingService from 'bookings-shared/lib/c1/schema/BookingService';
import { setMeetingTypeBookingUrlCodes } from '../actions/setMeetingTypeBookingUrlCodes';
/**
 * Update the meetingTypeEditState in the store with the meetingType that is being edited
 * so properties don't get lost on save.
 */
export function mapServiceToMeetingTypeEditState(meetingType: BookingService) {
    const defaultEnhancedLocation = {
        DisplayName: '',
        PostalAddress: undefined,
        Id: '',
    };
    const meetingTypeDescription = parseMeetingTypeDescription(meetingType.Description ?? '');
    const description = meetingTypeDescription.description;
    //TODO: assign getRandomEventThemeId to new meeting type
    const themeImageEnum =
        store.pbpPublishingSetting.publishingSettings.Theme?.PaletteId ?? DEFAULT_PALETTEID;
    setMeetingTypeName(meetingType.DisplayName ?? '');
    setMeetingTypeInboxReminders(convertInboxRemindersToGql(meetingType.InboxReminders ?? []));
    setMeetingTypeDescription(description);
    setMeetingTypeOrigin(meetingTypeDescription.origin ?? '');
    updateCurrentEventEmojiAction(meetingTypeDescription.emojiCode ?? '');
    updateCurrentEventThemeImageAction(themeImageEnum);
    setEditStatePrivacyType(meetingTypeDescription.privacyType ?? BookingPrivacyType.MeetingType);
    setMeetingTypeCharm(meetingType.Charm ?? 0);
    if (meetingType.ServiceId) {
        enableEditStateMeetingType(!meetingType.ExcludeFromSelfService);
    }
    setMeetingTypeDuration(meetingType.DefaultDurationMinutes ?? 30);
    setMeetingTypePreBuffer(meetingType.PreBuffer ?? 0);
    setMeetingTypePostBuffer(meetingType.PostBuffer ?? 0);
    setMeetingTypeLocation(meetingType.DefaultLocation ?? defaultEnhancedLocation);
    setOnlineMeetingLocation(meetingType.IsDefaultLocationOnline ?? false);
    // If meetingType has categories, show them as tags
    if (meetingType.CategoryNames) {
        const categories: string[] = meetingType.CategoryNames.map(category => category);
        setMeetingTypeCategory(categories ?? []);
    }
    if (meetingType.SchedulingPolicy) {
        setMeetingTypeMinLeadTime(meetingType.SchedulingPolicy.LeadTimeInMinutes ?? 60);
        setMeetingTypeMaxLeadTime(meetingType.SchedulingPolicy.EndOfBookingWindowInDays ?? 90);
        setMeetingTypeCustomTimeInterval(
            meetingType.SchedulingPolicy.TimeSlotIntervalInMinutes ?? 30
        );
        var serviceWindows = meetingType.SchedulingPolicy.ServiceBookableWindows;
        if (serviceWindows && serviceWindows.length > 0) {
            var isDateRestricted =
                serviceWindows.length === 2 &&
                !serviceWindows[0].StartString &&
                !serviceWindows[0].EndString &&
                serviceWindows[0].BookableHours?.every(day => day.TimeSlots?.length === 0);
            // Choose the meetingType window depending upon whether date restriction is active or not
            var serviceWindow = isDateRestricted ? serviceWindows[1] : serviceWindows[0];
            var serviceWindowHours = serviceWindow.BookableHours?.map(bh => {
                return {
                    index: bh.WorkDay as number,
                    timeSlots: bh.TimeSlots?.map(ts => {
                        return {
                            startMinutes: ts.StartTimeInMinutes,
                            endMinutes: ts.EndTimeInMinutes,
                        };
                    }),
                } as IWorkDay;
            });
            var startString;
            if (serviceWindow.StartString) {
                startString = owaDate(getUserTimeZone(), serviceWindow.StartString);
            }
            var endString;
            if (serviceWindow.EndString) {
                endString = owaDate(getUserTimeZone(), serviceWindow.EndString);
            }
            setMeetingTypeAvailability(
                {
                    availabilityType: AvailabilityType.Custom,
                    isDateRestricted,
                    startDate: startString,
                    endDate: endString,
                    customHours: serviceWindowHours,
                },
                false
            );
        } else {
            setMeetingTypeAvailability({ availabilityType: AvailabilityType.DefaultSetup }, false);
        }
    }
    setMeetingTypeBookingUrlCodes(meetingType.bookingUrlCodes ?? []);
}
